import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getStatusDisplay } from '../../activation-code/utils/activationCodeUtils';
import { useSummaryOptions, useUsedCodes } from '../hooks/useActivationReport';
import { useReportList } from '../hooks/useReportList';
import { activationReportService } from '../services/activationReportService';
import { BatchRow, ReportFilters, UsedCodeRow } from '../types';
import { exportFileName, exportList, usedCodesWorkbook } from '../utils/exportReport';
import { DELETED_USER, formatDateTime } from '../utils/format';
import ExportButton from './ExportButton';
import Pagination from './Pagination';
import ReportState from './ReportState';

interface UsedCodesDialogProps {
  batch: BatchRow | null;
  filters: ReportFilters;
  onClose: () => void;
}

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase';

const UserCell: React.FC<{ user: UsedCodeRow['user'] }> = ({ user }) => (
  <>
    <div className="text-gray-900">{user?.name || DELETED_USER}</div>
    {user && (user.phone || user.email) && <div className="text-xs text-gray-500">{user.phone || user.email}</div>}
  </>
);

const UsedCodesDialog: React.FC<UsedCodesDialogProps> = ({ batch, filters, onClose }) => {
  // Trang gắn với lô: đổi lô là về trang 1 ngay trong render đó, tránh một lần gọi API với trang cũ
  const [pageState, setPageState] = useState({ batchId: '', page: 1 });
  const batchId = batch?.batch_id ?? '';
  const page = pageState.batchId === batchId ? pageState.page : 1;

  const listFilters = { ...filters, batch_id: batchId, page };
  const query = useUsedCodes(listFilters, !!batch);
  const setPage = (p: number) => setPageState({ batchId, page: p });
  // Thanh trang dùng `page` của hộp thoại (nguồn thật), không dùng trang của dữ liệu đang giữ lại
  const { data, perPage, stateProps } = useReportList(query, listFilters, setPage);
  const { isPlaceholderData } = query;

  // keepPreviousData chỉ hợp lý khi chuyển trang trong cùng lô; sang lô khác thì dữ liệu giữ lại là của lô trước
  const loadedBatchRef = useRef('');
  useEffect(() => {
    if (data && !isPlaceholderData) loadedBatchRef.current = batchId;
  }, [data, isPlaceholderData, batchId]);
  const showingOtherBatch = !!data && isPlaceholderData && loadedBatchRef.current !== batchId;

  const summaryOptions = useSummaryOptions(filters.school_id);

  // Tên lô lấy từ dòng đã chọn: danh sách lô của bộ lọc có thể không chứa lô này
  const handleExport = () => {
    const exportF = { ...filters, batch_id: batchId };
    const options = { ...summaryOptions, batches: [{ id: batchId, name: batch?.name ?? batchId }] };
    return exportList(activationReportService.usedCodes, exportF, (items) => usedCodesWorkbook(items, exportF, options));
  };

  return (
    <Dialog open={!!batch} onOpenChange={(open) => {
        if (open) return;
        // Mở lại cùng lô phải bắt đầu từ trang 1
        setPageState({ batchId: '', page: 1 });
        onClose();
      }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{batch?.name}</DialogTitle>
          <DialogDescription>Mã đã dùng trong khoảng thời gian đã chọn</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            to={`/books/activation-codes/batch/${batchId}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Mở chi tiết lô
          </Link>
          <ExportButton onExport={handleExport} fileName={exportFileName('ma-da-dung', filters)} />
        </div>

        <ReportState {...stateProps} isLoading={stateProps.isLoading || showingOtherBatch}>
          {data && (
            <div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={TH}>Mã</th>
                      <th className={TH}>Người dùng</th>
                      <th className={TH}>Trường</th>
                      <th className={TH}>Ngày dùng</th>
                      <th className={TH}>Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.items.map((row) => {
                      const status = getStatusDisplay(row.status);
                      return (
                        <tr key={row.code}>
                          <td className="px-4 py-3 text-sm font-mono text-gray-900">{row.code}</td>
                          <td className="px-4 py-3 text-sm">
                            <UserCell user={row.user} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{row.school_name || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {formatDateTime(row.used_at)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.badgeClass}`}
                            >
                              {status.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                perPage={perPage}
                total={data.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </ReportState>
      </DialogContent>
    </Dialog>
  );
};

export default UsedCodesDialog;
